import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DeviceCatalogEntry,
  DeviceInstall,
  DeviceRemoval,
  HealthBand,
  Site,
  SiteComplianceDetail,
  SiteVendorAssignment,
  VendorDirectoryEntry,
  VendorVerificationStatus,
} from '@mvcp/contracts';
import { deviceLabel } from './device-label';
import {
  assignVendor,
  getDevices,
  getSiteCompliance,
  getSiteDevices,
  getSiteRemovals,
  getSiteVendors,
  getVendors,
  installDevice,
  removeSiteDevice,
  removeSiteVendor,
} from '../api';
// One owner for the band vocabulary, pill, score number and Framework card
// (Story 8.1) — the `name`-aware `BandPill` this page pioneered in Story 7.2 is
// now the shared signature.
import { BandPill, CoverageNote, FrameworkCard, ScoreValue, UNKNOWN_LABEL } from './bands';
import type { DrillTarget } from './CockpitShell';
import { ConfirmModal } from './ConfirmModal';
import { DeviceCategoryIcon } from './DeviceCategoryIcon';
// The Add Device dialog filters the SAME catalog rows the Devices page does, so it runs
// the SAME predicate, the SAME sort and the SAME window size rather than a mirror of
// them — a copy is how "Navigation" comes to mean one thing on the directory and another
// on the vessel that installs from it.
import { byModel, matchesFilter, PAGE_SIZE } from './DeviceDirectory';
import { Logo } from './Logo';
import { ProgressBar } from './ProgressBar';
import { ProposeDeviceForm } from './ProposeDeviceForm';
// Story 8.9: the pending-proposal marker has ONE owner.
import { ProposedTag } from './proposed-tag';
import { FormModal } from './FormModal';

/**
 * A cross-reference cell (Story 4.4): in cockpit mode (`onOpenRecord` present) the
 * name is a real `<button>` that drills to the neighbor record; standalone it is
 * plain text (the Epic-2 behavior, unchanged).
 */
function DrillName({
  name,
  target,
  onOpenRecord,
}: {
  name: string;
  target: DrillTarget;
  onOpenRecord?: (target: DrillTarget) => void;
}): React.JSX.Element {
  if (!onOpenRecord) return <>{name}</>;
  return (
    <button
      type="button"
      onClick={() => onOpenRecord(target)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: 'var(--text)',
        font: 'inherit',
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {name}
    </button>
  );
}

/**
 * Verification-status → pill tone. Inlined here (contracts is the source of
 * truth: VENDOR_VERIFICATION_STATUSES + the DESIGN.md status triads) because the
 * web consumes `@mvcp/contracts` as TYPES ONLY — importing a runtime value
 * breaks `vite build` (CJS __exportStar; Story 1.6 debug log). Keep in sync with
 * `packages/contracts/src/entities.ts` and `VendorDirectory.tsx`.
 */
const TONE: Record<VendorVerificationStatus, 'ok' | 'warn' | 'bad'> = {
  verified: 'ok',
  pending: 'warn',
  unverified: 'bad',
};
const VERIFICATION_LABEL: Record<VendorVerificationStatus, string> = {
  verified: 'Verified',
  pending: 'Pending',
  unverified: 'Unverified',
};

function VerificationPill({ status }: { status: VendorVerificationStatus }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={TONE[status]}>
      <span className="status-pill__dot" aria-hidden="true" />
      {VERIFICATION_LABEL[status]}
    </span>
  );
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; assignments: SiteVendorAssignment[] }
  | { kind: 'error'; message: string };

type DeviceState =
  | { kind: 'loading' }
  | { kind: 'ready'; installs: DeviceInstall[] }
  | { kind: 'error'; message: string };

type RemovalState =
  | { kind: 'loading' }
  | { kind: 'ready'; removals: DeviceRemoval[] }
  | { kind: 'error'; message: string };

type ComplianceState =
  | { kind: 'loading' }
  | { kind: 'ready'; detail: SiteComplianceDetail }
  | { kind: 'error'; message: string };

/**
 * The pill beside the Site name. It carries the Site's HEALTH band — the same value
 * the dashboard's Site-status row shows, so a Manager who clicks a red row can never
 * land on a green pill.
 *
 * While the read is in flight or after it fails the band is UNKNOWN, which is a
 * different claim from `neutral`/"Not assessed" ("we assessed this vessel and found
 * nothing to score"). Rendering the neutral label there would state, in grey, that a
 * genuinely `bad` vessel is unassessed — the same false-reassurance failure the story
 * forbids, just triggered by an error instead of by design. So the tone stays neutral
 * (nothing is being claimed) while the TEXT says plainly that the state is unknown.
 */
function SiteHealthPill({ state }: { state: ComplianceState }): React.JSX.Element {
  if (state.kind === 'ready') return <BandPill band={state.detail.band} name="Site health" />;
  return (
    <BandPill
      band="neutral"
      name="Site health"
      label={state.kind === 'loading' ? UNKNOWN_LABEL.loading : UNKNOWN_LABEL.error}
    />
  );
}

/**
 * One installed Device's compliance cell — the per-device expansion of the header pill.
 *
 * A vessel that reads red says only *that* it is red; this is the column that says WHICH
 * box. The verdict is read straight off `GET /dashboard/sites/:siteId` — the same engine
 * pass that produced the header band — so this cell can never disagree with the pill above
 * it, and the web does no arithmetic to render it.
 *
 * Three states that are NOT bands are deliberately not painted as one. While the read is
 * in flight, after it fails, and when the payload carries no row for a Device the page is
 * showing (a mount whose device list and compliance read are momentarily out of step), the
 * honest claim is "we do not know" — and `BAND_LABEL.neutral` there would state in
 * reassuring grey that a possibly-lapsed box has nothing to assess. `UNKNOWN_LABEL` is the
 * shared vocabulary for exactly that, borrowed from the header pill rather than reworded.
 *
 * `name` makes the pill a named group: a table of otherwise identical pills is unreadable
 * to assistive tech, and this row's verdict may legitimately differ from the vessel's.
 */
function DeviceComplianceCell({
  state,
  row,
  name,
  deactivated,
}: {
  state: ComplianceState;
  /** The server's verdict for this Device, or `undefined` when the payload has no row. */
  row: { band: HealthBand; detail: string | null } | undefined;
  name: string;
  /** Whether the owning Site is mothballed (DEV-4424 d). */
  deactivated: boolean;
}): React.JSX.Element {
  // On a deactivated Site the server returns `devices: []` BY DESIGN — its coverage was
  // filtered out of the engine, so there is no verdict to state for any install. Falling
  // through to the `!row` arm would label EVERY device row "Compliance unavailable", i.e.
  // claim a read failed when nothing failed — the same false-outage claim the Sites list
  // Status cell was corrected for. The Site-level "Deactivated" pill in the header already
  // explains the whole table, so the cells state nothing rather than stating an error.
  if (deactivated) {
    return <span aria-label={`${name}: not scored while the Site is deactivated`}>—</span>;
  }
  if (state.kind === 'loading') {
    return <BandPill band="neutral" name={name} label={UNKNOWN_LABEL.loading} />;
  }
  if (state.kind === 'error' || !row) {
    return <BandPill band="neutral" name={name} label={UNKNOWN_LABEL.error} />;
  }
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <BandPill band={row.band} name={name} />
      {/* A red cell is never unexplained — and a healthy one is never explained.
          `reasonFor` returns a reason for every non-neutral band, so a device in good
          standing arrived here carrying one too, and a twelve-device vessel printed
          twelve lines of restated good news at doubled row height. "Never unexplained"
          is a claim about the exceptions; it does not imply the rule needs a caption.
          So the driver line is suppressed for the top band IN THE CELL ONLY — the
          server's field stays truthful, and every other band still carries its reason.
          The server omits the device's own name from that line because the row's first
          column already spells it. */}
      {row.band !== 'ok' && row.detail && (
        <span className="muted" style={{ fontSize: 13 }}>
          {row.detail}
        </span>
      )}
    </span>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Full-page Site detail (Story 2.3, FR-5, UX drawer-vs-full-page). Opens in-pane
 * when a Site row is clicked (SitesView swaps `selectedSiteId`) — NOT a router,
 * NOT a drawer. Shows the Site header, its assigned Vendors (each referencing a
 * global backbone record) with an inline two-step Remove, and an assign picker
 * of directory Vendors not already assigned. The list refreshes after every
 * mutation and errors surface inline — a mutation never silently succeeds.
 *
 * Story 7.2 wires the Site's own compliance: the header pill and the Compliance
 * section are served by `GET /dashboard/sites/:siteId`, whose band and worst-driver
 * ARE the dashboard's Site-status row for this Site (same server-side engine pass, no
 * client arithmetic). The section fails independently — a rejected compliance read
 * shows an inline message and leaves the Vendors / Devices / Removal-History sections
 * working.
 */
export function SiteDetail({
  site,
  onBack,
  onOpenRecord,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  site: Site;
  onBack: () => void;
  /**
   * Drill to a spine neighbor (Story 4.4). Present in cockpit mode: an assigned
   * Vendor row opens that Vendor; an installed Device row opens that Device — each
   * pushed on the cockpit back stack so Back returns to this Site. Absent standalone
   * (the Epic-2 fleet view keeps its current behavior; the names render as text).
   */
  onOpenRecord?: (target: DrillTarget) => void;
  /**
   * Raise "edit this Site" to the owner of the Sites list (DEV-4424). Optional: the header
   * control renders only when it is supplied.
   *
   * This page deliberately does NOT own the edit dialog. Its `site` is a PROP read out of
   * `SitesView`'s loaded list, so a save made here could not refresh the very object the
   * page renders — only the list owner can re-read and hand down the new values. So the
   * dialog, the patch and the reload all live there, and this is just the affordance.
   */
  onEdit?: () => void;
  /**
   * Raise "deactivate this Site" / "reactivate this Site" to the owner of the Sites list
   * (DEV-4424 d). Optional, exactly like {@link onEdit}: each header control renders only
   * when its handler is supplied.
   *
   * Raised for the SAME reason `onEdit` is: `site` is a prop read out of `SitesView`'s
   * loaded list, so a transition confirmed here could not refresh the very object this page
   * renders — the header pill and the suppressed controls below all key off
   * `site.deactivatedAt`, and only the list owner can re-read it. So the `ConfirmModal`,
   * the POST and the reload live there, and this is just the affordance.
   */
  onDeactivate?: () => void;
  onReactivate?: () => void;
}): React.JSX.Element {
  /**
   * Whether this Site is mothballed (DEV-4424 d). `deactivatedAt` is OMITTED while active
   * and never null, so the whole lifecycle is this one test — and it is deliberately not
   * `site.status`, which is the compliance rollup pill and says nothing about lifecycle.
   *
   * A deactivated Site stays fully READABLE: this page loads, and its vendor, device,
   * removal-history and compliance sections all render unchanged. What it loses is every
   * WRITE affordance, because the API answers each of those 409 — an Edit or Add control
   * that can only produce a server error is a dead control, not a disabled one.
   */
  const deactivated = Boolean(site.deactivatedAt);
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [directory, setDirectory] = useState<VendorDirectoryEntry[]>([]);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [assigning, setAssigning] = useState(false);
  /** Whether the assign dialog is open (the control moved out of the page body). */
  const [assigningVendor, setAssigningVendor] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // Two-step inline remove confirm: the vendorId currently awaiting confirm.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // --- Devices + Removal History (Story 2.4) ---
  const [deviceState, setDeviceState] = useState<DeviceState>({ kind: 'loading' });
  const [catalog, setCatalog] = useState<DeviceCatalogEntry[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  /** Whether the Add Device dialog is open (the picker moved out of the page body). */
  const [addingDevice, setAddingDevice] = useState(false);
  /**
   * The dialog's own filter state, modelled on the Device Directory's toolbar. It lives
   * on the page rather than in the dialog so a failed add — which keeps the dialog open —
   * keeps the Manager exactly where they were, filters and all.
   */
  const [deviceCategory, setDeviceCategory] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [showAllDevices, setShowAllDevices] = useState(false);
  /** The catalog row the last add attempt named — kept on failure, so the choice survives. */
  const [devicePick, setDevicePick] = useState('');
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  /** Whether a hand-pressed catalog retry is in flight (see `retryCatalog`). */
  const [catalogRetrying, setCatalogRetrying] = useState(false);
  const [removalState, setRemovalState] = useState<RemovalState>({ kind: 'loading' });
  // The DeviceInstall currently awaiting modal-confirm for removal (null = closed).
  const [deviceToRemove, setDeviceToRemove] = useState<DeviceInstall | null>(null);
  const [removingDevice, setRemovingDevice] = useState(false);
  const [deviceRemoveError, setDeviceRemoveError] = useState<string | null>(null);
  /**
   * Whether the Add Device dialog is showing its PROPOSE mode instead of the browse list
   * (Story 2.5). One dialog, two modes — never a second `FormModal`: `ProposeDeviceForm`
   * renders a bare `<form>` with its own submit/cancel and no heading, so it drops into
   * this body once the list is swapped out, whereas nesting would stack two
   * `useDialogA11y` focus traps on one screen.
   */
  const [proposingDevice, setProposingDevice] = useState(false);
  // Whether the post-propose vendor re-read is in flight (review round 1). The propose
  // affordance is inert for that window so the form cannot be re-opened onto a picker that
  // does not yet hold the vendor this page just minted.
  const [directoryReloading, setDirectoryReloading] = useState(false);
  // What the last propose from THIS page created (Story 8.9) — the device, and the vendor
  // when the inline path minted one. This mount discarded the entry entirely and printed
  // nothing at all, so a Manager who proposed from here got a silent success and had to
  // go looking in the picker to find out whether anything had happened.
  const [proposedDeviceNotice, setProposedDeviceNotice] = useState<{
    /** The created row's id, so the acknowledgement can CHECK the list it points at. */
    deviceId: string;
    device: string;
    vendor?: string;
  } | null>(null);
  /**
   * Bumped once per Add Device MODE SWAP, which is what the focus effect below reacts to.
   */
  const [deviceModeSwaps, setDeviceModeSwaps] = useState(0);

  /**
   * Move focus deliberately when the dialog replaces its own body.
   *
   * `useDialogA11y` focuses the panel once when a dialog opens and restores the opener when
   * it closes; it knows nothing about a dialog that swaps its contents while staying open.
   * So switching browse ⇄ propose unmounted the control holding focus and dropped focus to
   * `<body>` — outside the trap, with the next Tab starting from the top of the document —
   * while the dialog's `aria-labelledby` title changed with nothing announcing it.
   *
   * Re-focusing the PANEL answers both at once: the panel is the element that title names,
   * so moving focus to it is what makes the new title spoken, and the trap gets its anchor
   * back. The panel is reached through the heading id this page owns rather than through a
   * ref, because `FormModal` keeps its panel ref private (and one shared dialog shell
   * growing a focus-management API for one caller is the worse trade).
   */
  useEffect(() => {
    if (deviceModeSwaps === 0) return;
    document
      .getElementById('add-device-heading')
      ?.closest<HTMLElement>('[role="dialog"]')
      ?.focus();
  }, [deviceModeSwaps]);

  /**
   * Open or close the propose form, retiring any previous acknowledgement (review round 1).
   * A bare `setProposingDevice((p) => !p)` left the notice standing, so proposing once,
   * re-opening the form and then FAILING showed the red error and the "was proposed" line
   * together — the failure reading as a success. `DeviceDirectory`'s `toggleProposing` has
   * always cleared it; this mount copied the markup and the docblock but not the invariant.
   */
  function toggleProposingDevice(next: boolean): void {
    setProposingDevice(next);
    setProposedDeviceNotice(null);
    // The failed-add error belongs to the flow being LEFT. It renders outside the mode
    // switch (a catalog failure is a fact about the list in either mode), so leaving it
    // standing mounted the propose form beneath a red `aria-live="assertive"` error about
    // an add nobody is attempting any more — the same stale-state-across-a-mode-change
    // failure rounds 1 and 3 fixed for the acknowledgement, from the other side.
    setInstallError(null);
    setDeviceModeSwaps((n) => n + 1);
  }

  /** Open the Add Device dialog on its browse mode, with nothing stale carried in. */
  function openAddDevice(): void {
    setInstallError(null);
    setProposedDeviceNotice(null);
    setProposingDevice(false);
    setAddingDevice(true);
  }

  /**
   * Dismiss the whole dialog — scrim, Esc and the close button alike. Everything the
   * dialog owns is retired with it: a filter, a half-typed search, a failed-add error or
   * a propose acknowledgement that survived a close would come back on the next open with
   * no cause the Manager could see.
   */
  function closeAddDevice(): void {
    setAddingDevice(false);
    setProposingDevice(false);
    setProposedDeviceNotice(null);
    setInstallError(null);
    setDevicePick('');
    setDeviceCategory('');
    setDeviceSearch('');
    setShowAllDevices(false);
  }

  /**
   * The DISMISS gesture — the scrim, Esc and the close button, none of which know what the
   * dialog is doing when they fire.
   *
   * It refuses while a mutation is in flight. Unguarded, an Esc or a stray scrim click
   * during the add POST (or during the post-propose re-read) unmounted the dialog, and the
   * outcome then had nowhere to be reported: the row would appear on the next refresh with
   * no acknowledgement, or the failure would be swallowed entirely — a silent mutation,
   * which is the one thing this page's every other control is built not to do. The
   * `ConfirmModal` below already guards its own cancel the same way (`if (removingDevice)
   * return;`); this is that precedent applied to the dialog that mutates more.
   */
  function dismissAddDevice(): void {
    if (installing || directoryReloading) return;
    closeAddDevice();
  }

  /**
   * Re-run the catalog read, from inside the dialog that needs it.
   *
   * `loadCatalog` ran on mount and on a propose success and nowhere else, so a
   * `GET /devices` that failed at mount left EVERY subsequent open of this dialog showing
   * the same frozen error over an empty list, with a full page reload the only way out.
   * The failure is reported here, so the recovery belongs here too.
   */
  async function retryCatalog(): Promise<void> {
    setCatalogRetrying(true);
    try {
      await loadCatalog();
    } finally {
      setCatalogRetrying(false);
    }
  }

  // --- This Site's compliance (Story 7.2) ---
  const [complianceState, setComplianceState] = useState<ComplianceState>({ kind: 'loading' });
  // Monotonic read counter. Every mutation on this page re-reads compliance, and
  // assign and install are separate controls with separate in-flight flags, so two
  // reads can overlap; if the earlier one resolves last it would reinstate the
  // PRE-mutation verdict permanently. The other loaders on this page carry the same
  // shape, but they publish lists — this one publishes a compliance judgement, where
  // "quietly stale" is the exact failure the section exists to prevent.
  const complianceRead = useRef(0);

  const loadAssignments = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const assignments = await getSiteVendors(site.id);
      setState({ kind: 'ready', assignments });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, [site.id]);

  const loadDirectory = useCallback(async () => {
    setDirectoryError(null);
    try {
      setDirectory(await getVendors());
    } catch (err: unknown) {
      // A directory load failure must NOT fail silently: without vendors to choose,
      // assignment is impossible, so surface it inline (the component never silently
      // succeeds/fails a mutation path).
      //
      // The last good list STANDS (review round 2). Story 8.9 newly calls this on the propose
      // SUCCESS path, and blanking `directory` there emptied the Vendor-ASSIGN picker in a
      // different region of the page — so a Manager saw a propose success notice in Devices
      // and an emptied picker plus "Couldn't load the vendor directory" in Vendors, with no
      // causal link between them. On first load `directory` is already `[]`.
      setDirectoryError(
        err instanceof Error ? err.message : "Couldn't load the vendor directory.",
      );
    }
  }, []);

  const loadDevices = useCallback(async () => {
    setDeviceState({ kind: 'loading' });
    try {
      const installs = await getSiteDevices(site.id);
      setDeviceState({ kind: 'ready', installs });
    } catch (err: unknown) {
      setDeviceState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to load',
      });
    }
  }, [site.id]);

  const loadCatalog = useCallback(async () => {
    setCatalogError(null);
    try {
      setCatalog(await getDevices());
    } catch (err: unknown) {
      // A catalog load failure leaves the install picker empty; surface it inline
      // (installing is impossible without devices — never fail silently).
      //
      // The last good list STANDS, exactly as in `loadDirectory` above (review round 3).
      // Round 2 fixed the sibling and left this one blanking, and Story 8.9 put BOTH on the
      // propose SUCCESS path (`Promise.all([loadCatalog(), loadDirectory()])`) — so a
      // propose that succeeded followed by a dropped `GET /devices` emptied the install
      // picker while the fresh acknowledgement beside it said "Install it from the picker
      // above". On first load `catalog` is already `[]`.
      setCatalogError(
        err instanceof Error ? err.message : "Couldn't load the device catalog.",
      );
    }
  }, []);

  const loadRemovals = useCallback(async () => {
    setRemovalState({ kind: 'loading' });
    try {
      const removals = await getSiteRemovals(site.id);
      setRemovalState({ kind: 'ready', removals });
    } catch (err: unknown) {
      setRemovalState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to load',
      });
    }
  }, [site.id]);

  const loadCompliance = useCallback(async () => {
    const read = (complianceRead.current += 1);
    // A superseded read must not publish: it carries a verdict from before the
    // mutation that replaced it.
    const stale = (): boolean => read !== complianceRead.current;
    setComplianceState({ kind: 'loading' });
    try {
      const detail = await getSiteCompliance(site.id);
      if (stale()) return;
      setComplianceState({ kind: 'ready', detail });
    } catch (err: unknown) {
      if (stale()) return;
      // Section-local: the compliance read failing must not take the page down —
      // assign/install/removal all keep working (and the header keeps its pill).
      setComplianceState({
        kind: 'error',
        message: err instanceof Error ? err.message : "Couldn't load this site's compliance.",
      });
    }
  }, [site.id]);

  useEffect(() => {
    void loadAssignments();
    void loadDirectory();
    void loadDevices();
    void loadCatalog();
    void loadRemovals();
    void loadCompliance();
  }, [loadAssignments, loadDirectory, loadDevices, loadCatalog, loadRemovals, loadCompliance]);

  const assignments = state.kind === 'ready' ? state.assignments : [];
  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => a.vendor.id)),
    [assignments],
  );
  // Only offer directory Vendors not already assigned to this Site.
  const assignable = useMemo(
    () => directory.filter((e) => !assignedIds.has(e.vendor.id)),
    [directory, assignedIds],
  );

  const installs = deviceState.kind === 'ready' ? deviceState.installs : [];
  const installedDeviceIds = useMemo(
    () => new Set(installs.map((i) => i.device.id)),
    [installs],
  );
  // Only offer catalog Devices not already installed on this Site.
  const installable = useMemo(
    () => catalog.filter((e) => !installedDeviceIds.has(e.device.id)),
    [catalog, installedDeviceIds],
  );

  // Categories present in the OFFERABLE rows, first-seen order — the Device Directory's
  // convention (`DeviceDirectory.tsx`), not the closed `DEVICE_CATEGORY_OPTIONS` list: a
  // dropdown offering a category no visible row carries can only lead to the no-matches
  // state, which reads as a broken filter rather than an empty shelf.
  const addableCategories = useMemo(() => {
    const seen: string[] = [];
    for (const e of installable) {
      const c = e.device.category;
      if (c && !seen.includes(c)) seen.push(c);
    }
    return seen;
  }, [installable]);

  const deviceQuery = deviceSearch.trim().toLowerCase();
  const filteredInstallable = useMemo(
    () =>
      [...installable.filter((e) => matchesFilter(e, deviceCategory, deviceQuery))].sort(byModel),
    [installable, deviceCategory, deviceQuery],
  );
  const visibleInstallable = showAllDevices
    ? filteredInstallable
    : filteredInstallable.slice(0, PAGE_SIZE);
  const hasDeviceFilter = deviceCategory !== '' || deviceQuery !== '';
  /**
   * Reset the dialog's filters. `showAll` also lifts the `PAGE_SIZE` window — see
   * `onProposedDevice`, where clearing the filters alone was not enough to put a freshly
   * proposed row inside the list the acknowledgement points at.
   */
  function clearDeviceFilters(showAll = false): void {
    setDeviceCategory('');
    setDeviceSearch('');
    setShowAllDevices(showAll);
  }

  /**
   * Is the row the propose acknowledgement names actually in the list it points at?
   *
   * "Add it from the list below" is a claim about what is on screen, so it is CHECKED
   * against the rendered window rather than inferred from the POST having succeeded. Two
   * ways it is false: the post-propose `loadCatalog` failed, so the catalog provably does
   * not hold the row (`proposedInCatalog` false); or a filter typed since hides it
   * (`proposedInCatalog` true, `proposedRowVisible` false). The two get different wording
   * because they have different remedies.
   */
  const proposedInCatalog =
    proposedDeviceNotice !== null &&
    catalog.some((e) => e.device.id === proposedDeviceNotice.deviceId);
  const proposedRowVisible =
    proposedDeviceNotice !== null &&
    visibleInstallable.some((e) => e.device.id === proposedDeviceNotice.deviceId);

  /**
   * This Site's per-DEVICE compliance, keyed by device id (Story: vessel device state).
   * Server-computed inside the SAME `assembleSiteCompliance` pass that produced the
   * header band, so a row can never disagree with the vessel it sits on — the web does
   * no arithmetic here, it only looks the verdict up.
   */
  const deviceBands = useMemo(() => {
    const map = new Map<string, { band: HealthBand; detail: string | null }>();
    if (complianceState.kind === 'ready') {
      for (const d of complianceState.detail.devices) {
        map.set(d.deviceId, { band: d.band, detail: d.detail });
      }
    }
    return map;
  }, [complianceState]);

  async function onAssign(): Promise<void> {
    setAssignError(null);
    if (!pick) {
      setAssignError('Select a vendor to assign.');
      return;
    }
    setAssigning(true);
    try {
      await assignVendor(site.id, pick);
      setPick('');
      // Close on SUCCESS only — a failure keeps the dialog, its selection and its error.
      setAssigningVendor(false);
      // Compliance too: this Site's subjects just changed, so its band and score
      // are stale the instant the mutation lands. Leaving them would show a
      // confident pre-mutation verdict above the control that invalidated it.
      await Promise.all([loadAssignments(), loadCompliance()]);
    } catch (err: unknown) {
      setAssignError(err instanceof Error ? err.message : "Couldn't assign. Try again.");
    } finally {
      setAssigning(false);
    }
  }

  async function onConfirmRemove(vendorId: string): Promise<void> {
    setRemoveError(null);
    setRemoving(true);
    try {
      await removeSiteVendor(site.id, vendorId);
      setConfirmId(null);
      await Promise.all([loadAssignments(), loadCompliance()]);
    } catch (err: unknown) {
      setRemoveError(err instanceof Error ? err.message : "Couldn't remove. Try again.");
    } finally {
      setRemoving(false);
    }
  }

  /**
   * Add one catalog Device to this Site, from its row in the dialog's list.
   *
   * The dialog closes on SUCCESS only. A failed POST keeps it open with its inline
   * `role="alert"`, its filters and the row that was chosen (`devicePick`) — the Manager
   * retries from where they were rather than re-finding the device behind a filter they
   * would have to type again.
   *
   * The propose acknowledgement is retired the moment the Manager acts on it: its whole
   * job is to point at this list, so once it has been acted on, leaving it up would put
   * "…was proposed…" directly above the error from the add that just failed — a failure
   * reading as a success.
   */
  async function onAddDevice(deviceId: string): Promise<void> {
    setInstallError(null);
    setProposedDeviceNotice(null);
    setDevicePick(deviceId);
    setInstalling(true);
    try {
      await installDevice(site.id, deviceId);
      setDevicePick('');
      closeAddDevice();
      // Compliance too: the added Device brings its certificates onto this Site, so the
      // band, the score AND every per-device row are stale the instant this lands.
      await Promise.all([loadDevices(), loadCompliance()]);
    } catch (err: unknown) {
      setInstallError(err instanceof Error ? err.message : "Couldn't add. Try again.");
    } finally {
      setInstalling(false);
    }
  }

  async function onConfirmRemoveDevice(): Promise<void> {
    if (!deviceToRemove) return;
    setDeviceRemoveError(null);
    setRemovingDevice(true);
    try {
      await removeSiteDevice(site.id, deviceToRemove.device.id);
      setDeviceToRemove(null);
      // Refresh installs AND removal history — a removal writes a history row —
      // AND compliance: the removed device's certificates left this Site with it.
      await Promise.all([loadDevices(), loadRemovals(), loadCompliance()]);
    } catch (err: unknown) {
      setDeviceRemoveError(err instanceof Error ? err.message : "Couldn't remove. Try again.");
    } finally {
      setRemovingDevice(false);
    }
  }

  // A Device was proposed into the catalog: reload the catalog so it appears in
  // the install picker (do NOT auto-install — install stays a separate action),
  // acknowledge what was created, and collapse the form.
  //
  // Story 8.9 adds the vendor DIRECTORY reload beside the catalog one, and it is not
  // housekeeping: this page fetches vendors exactly once (`loadDirectory`, on mount), so
  // after an inline propose mints a Vendor the propose form's picker would still be
  // missing it — and the Manager's cheapest next move would be to mint a SECOND copy of
  // the vendor they just created. The picker must be correct after an inline success or
  // the "read the directory first" mitigation turns into its opposite.
  //
  // The form COLLAPSES LAST, after the re-reads land (review round 1). The spec's wording is
  // "re-fetches vendors ... BEFORE the form collapses", and this mount had no incidental
  // protection: unlike `DeviceDirectory`, whose propose buttons are disabled while
  // `vendorsLoading`, nothing here stopped the Manager re-opening the form during the
  // re-read and seeing a picker that was still missing the vendor just minted — the exact
  // second-copy hazard the re-read exists to prevent.
  //
  // TWO controls have to be inert for that window, and round 2 found only one of them was.
  // `directoryReloading` disables the TOGGLE that re-opens the form. The form's OWN submit
  // button is disabled by `ProposeDeviceForm`'s `submitting`, which now spans this function
  // because `onCreated` is awaited — without that, the collapse-last ordering left the
  // mounted form submittable for two round trips and a second click minted the second copy
  // directly, which is worse than the stale picker the ordering was changed to prevent.
  async function onProposedDevice(
    entry: DeviceCatalogEntry,
    mintedVendor: boolean,
  ): Promise<void> {
    setProposedDeviceNotice({
      deviceId: entry.device.id,
      device: deviceLabel(entry.device),
      // Name the vendor ONLY when this propose minted it: on the picker path the Manager
      // chose the vendor themselves and does not need telling which one.
      ...(mintedVendor ? { vendor: entry.vendor.name } : {}),
    });
    setDirectoryReloading(true);
    try {
      await Promise.all([loadCatalog(), loadDirectory()]);
    } finally {
      setDirectoryReloading(false);
      // Back to the dialog's browse mode with the filters cleared, so the row that was
      // just created is actually visible in the list the acknowledgement points at —
      // `DeviceDirectory.onProposed` clears them for the same reason. The dialog itself
      // stays OPEN: proposing never installs, and the next action is one click away.
      setProposingDevice(false);
      // The mode swap unmounts the form that held focus — see the focus effect.
      setDeviceModeSwaps((n) => n + 1);
      // The WINDOW is lifted too, not just the filters: the list is capped at `PAGE_SIZE`
      // and sorted by model, so on any catalog larger than a page the row just created can
      // sort outside the visible window — and an acknowledgement pointing at a list that
      // does not contain the row is the silent success this notice exists to end.
      clearDeviceFilters(true);
    }
  }

  const detail = site.type === 'ship' ? site.imo : site.city;

  return (
    <>
      <button
        type="button"
        className="icon-button"
        onClick={onBack}
        style={{ alignSelf: 'flex-start' }}
      >
        {/* Cockpit Back returns to the prior record/section (dynamic), so use a neutral
            label; standalone Back always returns to the Sites list. */}
        {onOpenRecord ? '← Back' : '← Back to sites'}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          {site.name}
        </h1>
        {/* Story 7.2 — the Site's HEALTH band (see SiteHealthPill: an unknown state
            says so rather than borrowing the "Not assessed" label). */}
        <SiteHealthPill state={complianceState} />
        {/* The LIFECYCLE pill (DEV-4424 d), beside the health pill and deliberately not
            instead of it: the two answer different questions, and a deactivated Site's
            health pill legitimately reads neutral/"Not assessed" precisely BECAUSE its
            coverage left the engine — this is the pill that says why. The literal
            `status-pill` markup, because `BandPill`/`StatusPill` are keyed to the
            compliance vocabulary and "deactivated" is not a compliance verdict. */}
        {deactivated && (
          <span className="status-pill" data-tone="neutral">
            <span className="status-pill__dot" aria-hidden="true" />
            Deactivated
          </span>
        )}
        {/* Named for the Site it edits, so it never collides by name with the dialog's
            own "Save Site" submit (DEV-4424). Absent while deactivated — `PATCH /sites/:id`
            is 409 on a mothballed Site, so the dialog could only end in a server error. */}
        {onEdit && !deactivated && (
          <button type="button" className="icon-button" onClick={onEdit} aria-label={`Edit ${site.name}`}>
            Edit
          </button>
        )}
        {/* The lifecycle control, named for its Site so it can never collide by name with
            the confirm dialog's own "Deactivate site" / "Reactivate site" button. Exactly
            one of the two is offered, decided by the same `deactivated` test everything
            else on this page reads. */}
        {onDeactivate && !deactivated && (
          <button
            type="button"
            className="icon-button"
            onClick={onDeactivate}
            aria-label={`Deactivate ${site.name}`}
          >
            Deactivate
          </button>
        )}
        {onReactivate && deactivated && (
          <button
            type="button"
            className="icon-button"
            onClick={onReactivate}
            aria-label={`Reactivate ${site.name}`}
          >
            Reactivate
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: 0, textTransform: 'capitalize' }}>
        {site.type}
        {detail ? (
          <>
            {' · '}
            <span data-numeric={site.type === 'ship' ? true : undefined}>{detail}</span>
          </>
        ) : null}
      </p>

      {/* This Site's compliance (Story 7.2). Two DIFFERENT measurements live here and
          may legitimately disagree — the header pill is the Site's worst-of HEALTH,
          this percentage is its compliant ÷ applicable SCORE. Both are computed by the
          one server-side engine; this view only presents them. */}
      <section className="card stack" aria-labelledby="site-compliance-heading">
        <h2 id="site-compliance-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Compliance
        </h2>

        {complianceState.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        )}

        {complianceState.kind === 'error' && (
          <p className="muted" style={{ margin: 0 }}>{complianceState.message}</p>
        )}

        {complianceState.kind === 'ready' && (
          <>
            <div>
              <div className="muted" style={{ fontSize: 13 }}>Site score</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {/* "Site score —" announces as a bare "Site score" (screen readers
                    drop the em-dash), so an unscored Site says so in words — matching
                    the ProgressBar's own wording below. `ScoreValue` also carries the
                    `role="img"` that makes the name audible at all. */}
                <ScoreValue score={complianceState.detail.score} name="Site score" />
                {/* Named: this is the SCORE band, distinct from the health pill in
                    the header, which may legitimately read differently. */}
                <BandPill band={complianceState.detail.score.band} name="Site score" />
                <span className="muted" style={{ fontSize: 13 }} data-numeric>
                  {complianceState.detail.score.compliant} of{' '}
                  {complianceState.detail.score.applicable} compliant
                </span>
                {/* AD-22: this vessel's suppliers/devices that carry NO evidence are in
                    the denominator, so the coverage is what explains a low percentage —
                    or a `90%` that still reads `Action Needed`. Not announced —
                    `ScoreValue`'s name above is the single spoken source (this region
                    already renders the same value three ways). */}
                <CoverageNote score={complianceState.detail.score} />
              </div>
              <div style={{ marginTop: 8 }}>
                <ProgressBar
                  percentage={complianceState.detail.score.percentage}
                  band={complianceState.detail.score.band}
                  label="Site score"
                />
              </div>
            </div>

            {complianceState.detail.worstDriver && (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                {complianceState.detail.worstDriver}
              </p>
            )}

            {complianceState.detail.frameworks.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                No frameworks apply to your organization yet.
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {complianceState.detail.frameworks.map((f) => (
                  // No `onOpen`: drilling from a Site into a Framework is not part of
                  // this surface (the Framework detail is company-wide), so the shared
                  // card renders its static `role="group"` variant.
                  <FrameworkCard key={f.frameworkId} code={f.code} name={f.name} score={f.score} />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* `!deactivated` as well as the hidden trigger (DEV-4424 d): the Add controls are
          already absent on a mothballed Site, so this only matters when the Site FLIPS to
          deactivated while the dialog is open — a second tab, or another Manager. Without
          it the open dialog keeps a live Assign/Propose button whose only outcome is 409. */}
      {assigningVendor && !deactivated && (
      <FormModal title="Add Vendor" titleId="assign-vendor-heading" onClose={() => setAssigningVendor(false)}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
          <label className="stack" style={{ gap: 4, flex: '1 1 240px' }}>
            <span className="field-label">Vendor</span>
            <select
              aria-label="Vendor to assign"
              value={pick}
              onChange={(e) => {
                setPick(e.target.value);
                setAssignError(null);
              }}
              style={selectStyle}
            >
              <option value="">Select a vendor…</option>
              {assignable.map((e) => (
                <option key={e.vendor.id} value={e.vendor.id}>
                  {e.vendor.name}
                  {e.vendor.category ? ` — ${e.vendor.category}` : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void onAssign()}
            disabled={assigning}
          >
            {assigning ? 'Assigning…' : 'Assign'}
          </button>
        </div>
        {assignable.length === 0 && directory.length > 0 && (
          <p className="muted" style={{ margin: 0 }}>
            Every directory vendor is already assigned to this site.
          </p>
        )}
        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {(assignError ?? directoryError) && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {assignError ?? directoryError}
            </span>
          )}
        </div>
      </FormModal>
      )}

      <section className="card stack" aria-labelledby="assigned-vendors-heading">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="assigned-vendors-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Assigned Vendors
          </h2>
          {/* Absent while the Site is deactivated (DEV-4424 d), not disabled:
              `POST /sites/:id/vendors` answers 409 on a mothballed Site, so the dialog
              could only ever end in a server error. The LIST below is untouched — the
              assignments are kept and still shown; the Site is read-only, not invisible. */}
          {!deactivated && (
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => {
              setAssignError(null);
              setAssigningVendor(true);
            }}
          >
            {/* Plain language, not the system's verb: a Manager adds a supplier to a
                vessel — "assign" is what the row in `site_vendor` does. */}
            Add Vendor
          </button>
          )}
        </div>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && <p className="muted" style={{ margin: 0 }}>{state.message}</p>}

        {state.kind === 'ready' && assignments.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            No vendors assigned yet — assign one above.
          </p>
        )}

        {state.kind === 'ready' && assignments.length > 0 && (
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Verification</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.vendor.id}>
                  <td>
                    {/* The identity tile is a SIBLING of the drill button, never its
                        child: inside, the monogram/logo would join the button's
                        accessible name and "Furuno Electric" would stop being an exact
                        match. `Logo` is aria-hidden by default for the same reason — the
                        name is spelled in full immediately beside it. */}
                    <span className="cell-identity">
                      <Logo
                        id={a.vendor.id}
                        name={a.vendor.name}
                        {...(a.vendor.logo ? { logo: a.vendor.logo } : {})}
                      />
                      <DrillName name={a.vendor.name} target={{ kind: 'vendor', id: a.vendor.id }} onOpenRecord={onOpenRecord} />
                    </span>
                  </td>
                  <td>{a.vendor.category ?? '—'}</td>
                  <td>
                    <span className="cell-cluster">
                      <VerificationPill status={a.vendor.verificationStatus} />
                      {a.vendor.proposedByCompanyId ? <ProposedTag /> : null}
                    </span>
                  </td>
                  <td data-align="right">
                    {/* Absent while the Site is deactivated (DEV-4424 d), for the same
                        reason the Add controls are: `DELETE /sites/:id/vendors/:vendorId`
                        is a 409 on a mothballed Site, so a Remove offered here could only
                        ever end in an error. A deactivated Site exposes NO write control —
                        the assignment itself is deliberately retained so reactivating
                        restores the Site exactly as it was. */}
                    {deactivated ? null : confirmId === a.vendor.id ? (
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          className="btn-accent"
                          data-surface="accent"
                          onClick={() => void onConfirmRemove(a.vendor.id)}
                          disabled={removing}
                        >
                          {removing ? 'Removing…' : 'Confirm remove'}
                        </button>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => {
                            setConfirmId(null);
                            setRemoveError(null);
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => {
                          setConfirmId(a.vendor.id);
                          setRemoveError(null);
                        }}
                        aria-label={`Remove ${a.vendor.name}`}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {removeError && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {removeError}
            </span>
          )}
        </div>
      </section>

      {/* ---- Add Device: ONE dialog, two modes ------------------------------
          The browse list and `ProposeDeviceForm` are two bodies of the SAME
          `FormModal`, never a modal inside a modal: `useDialogA11y` installs a focus
          trap per dialog, and two live traps on one screen is a keyboard dead end.
          The propose escape hatch used to sit stranded below an unfiltered `<select>`
          of the whole catalog; it is now the second mode of the control a Manager is
          already using when they discover the row is missing. */}
      {/* Same guard as the vendor dialog above, for the same flip-while-open case. */}
      {addingDevice && !deactivated && (
        <FormModal
          title={proposingDevice ? 'Propose a new device' : 'Add Device'}
          titleId="add-device-heading"
          onClose={dismissAddDevice}
          wide
        >
          {/* Say what was created (Story 8.9). Mounted for the dialog's whole life —
              empty until there is something to say — because a `role="status"` element
              that first APPEARS at the moment of the update is announced unreliably,
              which would make a propose a silent success for an AT user. While empty it
              is taken out of flow so `.stack`'s gap opens no dead strip. It sits ABOVE
              the list precisely so "add it from the list below" points somewhere. */}
          <p
            role="status"
            className="muted"
            style={
              proposedDeviceNotice
                ? { margin: 0, fontSize: 13 }
                : { position: 'absolute', width: 1, height: 1, overflow: 'hidden' }
            }
          >
            {proposedDeviceNotice && (
              <>
                {proposedDeviceNotice.vendor ? (
                  <>
                    <strong>{proposedDeviceNotice.device}</strong> and its manufacturer{' '}
                    <strong>{proposedDeviceNotice.vendor}</strong> were proposed and are
                    awaiting admin verification.{' '}
                  </>
                ) : (
                  <>
                    <strong>{proposedDeviceNotice.device}</strong> was proposed and is
                    awaiting admin verification.{' '}
                  </>
                )}
                {/* Where the new row IS — checked, not assumed (see `proposedRowVisible`).
                    Telling a Manager to add it "from the list below" when the catalog
                    re-read dropped is a success notice pointing at a provably empty
                    promise, which is worse than saying nothing. */}
                {proposedRowVisible
                  ? 'Add it from the list below to put it in service on this site.'
                  : proposedInCatalog
                    ? 'Clear the filter above to bring it back into the list, then add it to put it in service on this site.'
                    : 'It is not in the list below: the device catalog could not be re-read. Retry that read, then add it.'}
              </>
            )}
          </p>

          {/* Outside the mode switch: a catalog read that failed during a propose is a
              fact about the list this dialog is here to show, in either mode.
              BOTH errors render. `installError ?? catalogError` let a failed add MASK a
              broken catalog read — the Manager dismissed the add failure, found the list
              empty and had nothing on screen saying why — and the two are independent
              facts about two different requests. */}
          <div
            role="alert"
            aria-live="assertive"
            style={{ minHeight: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
          >
            {installError && (
              <span className="status-pill" data-tone="bad">
                <span className="status-pill__dot" aria-hidden="true" />
                {installError}
              </span>
            )}
            {catalogError && (
              <span className="status-pill" data-tone="bad">
                <span className="status-pill__dot" aria-hidden="true" />
                {catalogError}
              </span>
            )}
          </div>

          {/* The recovery path. Nothing re-ran `loadCatalog`, so a `GET /devices` that
              failed at mount was permanent for the life of the page. */}
          {catalogError && (
            <div>
              <button
                type="button"
                className="icon-button"
                onClick={() => void retryCatalog()}
                disabled={catalogRetrying}
              >
                {catalogRetrying ? 'Retrying…' : 'Retry loading the catalog'}
              </button>
            </div>
          )}

          {proposingDevice ? (
            <ProposeDeviceForm
              vendors={directory}
              // RETURNS the promise, so the form stays disabled until the re-reads land
              // (review round 2). `void` here left a multi-round-trip window in which a
              // second click re-sent the identical body and minted a second Vendor.
              onCreated={(entry, mintedVendor) => onProposedDevice(entry, mintedVendor)}
              onCancel={() => toggleProposingDevice(false)}
            />
          ) : (
            <>
              {/* The Device Directory's toolbar, on the dialog: the same category
                  dropdown and the same free-text search, running the same imported
                  predicate. An unfiltered `<select>` of the whole catalog was the
                  control this replaces.
                  It is rendered only when there is something to narrow: a filter bar over
                  an empty shelf can lead nowhere but the no-matches state, which reads as
                  a broken control rather than as an empty catalog. */}
              {installable.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <select
                  aria-label="Filter by category"
                  value={deviceCategory}
                  onChange={(e) => {
                    setDeviceCategory(e.target.value);
                    setShowAllDevices(false);
                  }}
                  style={selectStyle}
                >
                  <option value="">All categories</option>
                  {addableCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  type="search"
                  aria-label="Search devices by model or manufacturer"
                  placeholder="Search by model or manufacturer…"
                  value={deviceSearch}
                  onChange={(e) => {
                    setDeviceSearch(e.target.value);
                    setShowAllDevices(false);
                  }}
                  style={{ ...selectStyle, flex: '1 1 200px' }}
                />
                {hasDeviceFilter && (
                  <button type="button" className="icon-button" onClick={() => clearDeviceFilters()}>
                    Clear
                  </button>
                )}
              </div>
              )}

              {/* THREE reasons this list can be empty, and they are not interchangeable:
                  nobody has put anything in the catalog, everything in it is already
                  here, or the read failed. An empty catalog with no error used to render
                  the filter toolbar over nothing at all and say nothing — the one empty
                  state that reads as a broken screen rather than as an answer. */}
              {catalog.length === 0 && !catalogError && (
                <p className="muted" style={{ margin: 0 }}>
                  There are no devices in the catalog yet. Propose the one you need below.
                </p>
              )}

              {catalog.length === 0 && catalogError && (
                <p className="muted" style={{ margin: 0 }}>
                  The device catalog could not be read, so there is nothing to choose from.
                  Retry above, or propose a new device below.
                </p>
              )}

              {/* Nothing left to add — distinct from "your filter matched nothing", and
                  the state in which the propose affordance below matters most. */}
              {installable.length === 0 && catalog.length > 0 && (
                <p className="muted" style={{ margin: 0 }}>
                  Every device in the catalog is already on this site.
                </p>
              )}

              {installable.length > 0 && filteredInstallable.length === 0 && (
                <p className="muted" style={{ margin: 0 }}>
                  No devices match your filter or search.
                </p>
              )}

              {filteredInstallable.length > 0 && (
                <>
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>Version</th>
                          <th>Manufacturer</th>
                          <th>Category</th>
                          <th aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleInstallable.map((e) => (
                          <tr
                            key={e.device.id}
                            data-selected={devicePick === e.device.id ? 'true' : undefined}
                          >
                            <td>
                              {/* Same three-part shape as the vessel's own Devices table
                                  below, and for the same reason — under MORE pressure
                                  here, five columns inside a 600px dialog. See that
                                  comment for why the tag sits outside `.cell-identity`. */}
                              <span className="cell-cluster">
                                <span className="cell-identity">
                                  <DeviceCategoryIcon category={e.device.category} />
                                  <span className="cell-identity__name">{e.device.model}</span>
                                </span>
                                {e.device.proposedByCompanyId ? <ProposedTag /> : null}
                              </span>
                            </td>
                            <td>
                              {e.device.version ? <span data-numeric>{e.device.version}</span> : '—'}
                            </td>
                            <td>{e.vendor.name}</td>
                            <td>{e.device.category ?? '—'}</td>
                            <td data-align="right">
                              {/* Named through `deviceLabel`, the ONE web owner of a
                                  device's spoken identity (AD-25): two catalog rows
                                  differing only by revision must not offer two
                                  identically-named buttons. */}
                              <button
                                type="button"
                                className="btn-accent"
                                data-surface="accent"
                                onClick={() => void onAddDevice(e.device.id)}
                                disabled={installing}
                                aria-label={`Add ${deviceLabel(e.device)}`}
                              >
                                {installing && devicePick === e.device.id ? 'Adding…' : 'Add'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredInstallable.length > PAGE_SIZE && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span className="muted" style={{ fontSize: 13 }}>
                        Showing <span data-numeric>{visibleInstallable.length}</span> of{' '}
                        <span data-numeric>{filteredInstallable.length}</span>
                      </span>
                      {!showAllDevices && (
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setShowAllDevices(true)}
                        >
                          Show all
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* The propose escape hatch, reachable WITHOUT leaving the dialog — it
                  swaps this body for the form rather than opening a second modal.
                  `directoryReloading` is defensive: while the post-propose re-read is in
                  flight the form is still mounted, so this control is not on screen at
                  all — but the ordering that guarantees that lives in
                  `onProposedDevice`, and a disabled control is the cheaper guard. */}
              <div>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => toggleProposingDevice(true)}
                  // No `aria-expanded`: this control only exists in the dialog's BROWSE
                  // body, so the attribute could only ever announce `false` — it was
                  // stating "collapsed" about a disclosure that no longer exists. The
                  // control swaps the dialog's whole body (and its title), which is a
                  // navigation, not an expansion.
                  disabled={directoryReloading}
                >
                  {installable.length === 0 && catalog.length > 0
                    ? 'Device not in the catalog? Propose a new device'
                    : 'Propose a new device'}
                </button>
              </div>
            </>
          )}
        </FormModal>
      )}

      <section className="card stack" aria-labelledby="site-devices-heading">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="site-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Devices
          </h2>
          {/* Absent while the Site is deactivated (DEV-4424 d), for the same reason as
              Add Vendor above: `POST /sites/:id/devices` is 409 on a mothballed Site. The
              installed-device list, its per-device compliance cells and the removal
              history all keep rendering. */}
          {!deactivated && (
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={openAddDevice}
            /*
             * Gated on the READ this dialog subtracts from. `installable` is the catalog
             * minus what is already here, and "what is already here" is `[]` while this
             * read is loading OR failed — so with a broken `GET /sites/:id/devices` the
             * dialog listed devices that are already on the vessel as addable, and
             * "Every device in the catalog is already on this site" became unreachable
             * precisely when it was most likely true. Adding is idempotent server-side, so
             * the Manager got a success-shaped no-op: the worst possible answer.
             */
            disabled={deviceState.kind !== 'ready'}
          >
            {/* "Install" is what the server does to a `device_install` row; a Manager
                adds a box to a vessel. */}
            Add Device
          </button>
          )}
        </div>

        {deviceState.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        )}

        {deviceState.kind === 'error' && (
          <>
            <p className="muted" style={{ margin: 0 }}>{deviceState.message}</p>
            {/* Say why the control beside the heading is inert — a disabled button with
                no stated cause is indistinguishable from a broken one. */}
            <p className="muted" style={{ margin: 0 }}>
              Adding a device is unavailable until this list loads: without it the picker
              would offer devices that are already on this site.
            </p>
          </>
        )}

        {deviceState.kind === 'ready' && installs.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            No devices installed yet — add one above.
          </p>
        )}

        {deviceState.kind === 'ready' && installs.length > 0 && (
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Version</th>
                <th>Vendor</th>
                <th>Category</th>
                {/* AFTER Category, BEFORE the actions column: the two positional cell
                    assertions in the suite read `cell[1]` as the Version, and appending
                    here keeps that true. */}
                <th>Compliance</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {installs.map((i) => (
                <tr key={i.device.id}>
                  <td>
                    {/* The category glyph is a SIBLING of the drill button, never its
                        child: inside, it would join the button's accessible name and
                        "FAR-2228 Radar" would stop being an exact match (the cockpit
                        drill test queries it by exact name). It stays aria-hidden — the
                        category is also spelled out in its own cell.

                        THREE shapes, TWO wrappers. `.cell-identity` is `flex-wrap: nowrap`
                        — deliberately, so a tile never lands alone above its name — and it
                        was holding the tag as a third child, itself `white-space: nowrap`
                        inside a table that now carries six columns. So the cell's
                        min-content width was tile + longest word + the whole tag, on one
                        unbreakable line. The identity PAIR keeps the nowrap it needs; the
                        third shape hangs off `.cell-cluster`, which may wrap (that is what
                        its docblock says it is for).

                        And the name carries `.cell-identity__name`, because
                        `.cell-identity > :first-child` pins `flex: 0 0 auto` for the tile
                        — while `DeviceCategoryIcon` renders NOTHING for a device with no
                        recorded category, which promoted the name into that slot and made
                        a long model name unshrinkable: the exact failure the rule exists to
                        prevent, in the one case nobody looks at. */}
                    <span className="cell-cluster">
                      <span className="cell-identity">
                        <DeviceCategoryIcon category={i.device.category} />
                        <span className="cell-identity__name">
                          <DrillName name={i.device.model} target={{ kind: 'device', id: i.device.id }} onOpenRecord={onOpenRecord} />
                        </span>
                      </span>
                      {i.device.proposedByCompanyId ? <ProposedTag /> : null}
                    </span>
                  </td>
                  {/* Story 7.1 — identifiers use the monospace face; a device with
                      no recorded revision shows the em-dash placeholder. */}
                  <td>
                    {i.device.version ? <span data-numeric>{i.device.version}</span> : '—'}
                  </td>
                  <td>{i.vendor.name}</td>
                  <td>{i.device.category ?? '—'}</td>
                  <td>
                    <DeviceComplianceCell
                      state={complianceState}
                      row={deviceBands.get(i.device.id)}
                      /*
                       * Named through `deviceLabel`, the ONE web owner of a device's
                       * spoken identity (AD-25) — the same composer the dialog's Add
                       * button uses. The bare model made two revisions of one model on
                       * one vessel into two identically-named `role="group"` pills, so
                       * an AT user could not tell which unit was red: the column's
                       * entire purpose, lost in the one case it matters most.
                       */
                      name={`${deviceLabel(i.device)} compliance`}
                      deactivated={deactivated}
                    />
                  </td>
                  <td data-align="right">
                    {/* Absent while deactivated (DEV-4424 d) — the vendor Remove rationale,
                        applied to the install: removing a Device from a mothballed Site is
                        a 409, and the install row is retained on purpose. */}
                    {!deactivated && (
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => {
                          setDeviceToRemove(i);
                          setDeviceRemoveError(null);
                        }}
                        aria-label={`Remove ${i.device.model}`}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      <section className="card stack" aria-labelledby="removal-history-heading">
        <h2 id="removal-history-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Removal History
        </h2>

        {removalState.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        )}

        {removalState.kind === 'error' && (
          <p className="muted" style={{ margin: 0 }}>{removalState.message}</p>
        )}

        {removalState.kind === 'ready' && removalState.removals.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            No devices have been removed from this site yet.
          </p>
        )}

        {removalState.kind === 'ready' && removalState.removals.length > 0 && (
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Vendor</th>
                <th>Removed by</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {removalState.removals.map((r, idx) => (
                <tr
                  key={`${r.device.id}-${r.removedAt}-${idx}`}

                >
                  <td>{r.device.model}</td>
                  <td>{r.vendor.name}</td>
                  <td>{r.removedByEmail}</td>
                  <td data-numeric>
                    {new Date(r.removedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {deviceToRemove && (
        <ConfirmModal
          title="Remove device from site?"
          message={
            <>
              Removing <strong>{deviceToRemove.device.model}</strong> affects{' '}
              <strong>this site only</strong> — the global device record is kept and it
              stays installed on any other sites. This action is{' '}
              <strong>logged in Removal History</strong>.
            </>
          }
          confirmLabel="Remove device"
          busy={removingDevice}
          error={deviceRemoveError}
          onConfirm={() => void onConfirmRemoveDevice()}
          onCancel={() => {
            if (removingDevice) return;
            setDeviceToRemove(null);
            setDeviceRemoveError(null);
          }}
        />
      )}
    </>
  );
}
